import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Award, Users, Clock, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { services, projects, testimonials, stats, whyChooseUs } from '../data/mock';
import * as Icons from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[600px] flex items-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1769147555720-71fc71bfc216?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxtZWRpY2FsJTIwaW5mcmFzdHJ1Y3R1cmV8ZW58MHx8fHwxNzcwNzkyMDc3fDA&ixlib=rb-4.1.0&q=85"
            alt="Medical Infrastructure"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-slate-900/70"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10">
          <div className="max-w-3xl">
            <Badge className="mb-4 bg-blue-600 text-white border-0">Medical Services Experts</Badge>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Building Excellence in <span className="text-blue-400">Medical Services</span>
            </h1>
            <p className="text-xl text-slate-200 mb-8 leading-relaxed">
              Specialized solutions for  setting up MRI, CT, PET CT, Cathlab Room, providing Electrical Wiring,
Distribution Panel, Plumbing and HVAC systems and Civil and Turnkey projects with over a decade of expertise and commitment to quality.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white h-12 px-8">
                  Get a Quote <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/services">
                <Button size="lg" variant="outline" className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-slate-900 h-12 px-8">
                  Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-slate-800">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => {
              const IconComponent = Icons[stat.icon];
              return (
                <div key={index} className="text-center">
                  <div className="flex justify-center mb-3">
                    <div className="w-14 h-14 rounded-full bg-blue-600/20 flex items-center justify-center">
                      <IconComponent className="text-blue-400" size={28} />
                    </div>
                  </div>
                  <div className="text-4xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-slate-300">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-blue-50 text-blue-600 border-blue-200">About S2U Enterprises</Badge>
              <h2 className="text-4xl font-bold text-slate-900 mb-6">
                Your Trusted Partner in Medical Infrastructure
              </h2>
              <p className="text-slate-600 mb-6 leading-relaxed">
                With over a decade of experience, S2U Enterprises has established itself as a leading provider of medical infrastructure solutions, electrical services, and turnkey projects. Our commitment to excellence and customer satisfaction drives everything we do.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="text-blue-600 flex-shrink-0" size={20} />
                  <span>Specialized expertise in installation of MRI, CT scan rooms, PET-CT and CathLabs</span>
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="text-blue-600 flex-shrink-0" size={20} />
                  <span>24x7 service with 3-hour response time in Delhi & NCR</span>
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="text-blue-600 flex-shrink-0" size={20} />
                  <span>Commitment to quality standards and ethical practices</span>
                </li>
                <li className="flex items-center gap-3 text-slate-700">
                  <CheckCircle className="text-blue-600 flex-shrink-0" size={20} />
                  <span>24-72 hr report time for outside delhi.</span>
                </li>
              </ul>
              <Link to="/about">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  Learn More <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a"
                alt="Professional Team"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-blue-600 text-white p-6 rounded-xl shadow-xl">
                <div className="text-3xl font-bold mb-1">150+</div>
                <div className="text-blue-100">Successful Projects</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-50 text-blue-600 border-blue-200">Our Services</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Comprehensive Solutions</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Professional services for medical infrastructure, electrical systems, and turnkey projects
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => {
              const IconComponent = Icons[service.icon];
              return (
                <Card key={service.id} className="overflow-hidden border-slate-200 hover:shadow-lg transition-shadow">
                  <div className="relative h-48">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-12 h-12 rounded-lg bg-blue-600 text-white flex items-center justify-center mb-2">
                        <IconComponent size={24} />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{service.title}</h3>
                    <p className="text-slate-600 mb-4 text-sm leading-relaxed">{service.description}</p>
                    <ul className="space-y-2 mb-4">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle size={14} className="text-blue-600 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Link to="/contact">
                      <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white">
                        Request Service
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:border-blue-600 hover:text-blue-600">
                View All Services <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-50 text-blue-600 border-blue-200">Why Choose Us</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Our Commitment to Excellence</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We combine expertise, quality, and reliability to deliver exceptional results for every project.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, index) => {
              const IconComponent = Icons[item.icon];
              return (
                <div key={index} className="flex gap-4 p-6 rounded-xl bg-slate-50 hover:bg-blue-50 transition-colors">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-lg bg-blue-600 text-white flex items-center justify-center">
                      <IconComponent size={24} />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                    <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-50 text-blue-600 border-blue-200">Our Work</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Featured Projects</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Delivering excellence across premier medical institutions and healthcare facilities.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.slice(0, 3).map((project) => (
              <Card key={project.id} className="overflow-hidden group hover:shadow-xl transition-all duration-300">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  />
                  <Badge className="absolute top-4 left-4 bg-blue-600 text-white border-0">
                    {project.category}
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{project.title}</h3>
                  <p className="text-slate-600 mb-4 text-sm leading-relaxed">{project.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">{project.client}</span>
                    <span className="text-sm font-semibold text-blue-600">{project.year}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/projects">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white">
                View All Projects <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-50 text-blue-600 border-blue-200">Testimonials</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">What Our Clients Say</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Trusted by leading medical institutions and healthcare facilities across India.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={18} className="fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-6 italic leading-relaxed">"{testimonial.content}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-slate-200 overflow-hidden">
                      <img src={testimonial.image} alt={testimonial.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{testimonial.name}</div>
                      <div className="text-sm text-slate-600">{testimonial.designation}</div>
                      <div className="text-sm text-blue-600">{testimonial.company}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-slate-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Your Project?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help you with your medical infrastructure and technical needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-slate-100 h-12 px-8">
                Get in Touch <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
            <a href="tel:9560043484">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 h-12 px-8">
                Call Us Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
